// ─────────────────────────────────────────────────────────────
//  E-Mail-Vorlagen (no-code Generator)
//
//  Vom Team im Settings-Bereich angelegte Vorlagen steuern den
//  wiederverwendbaren E-Mail-Generator: Stil-Beispiel(e) + KI-Hinweis +
//  Drop-Felder + Ziel-Phase(n). Daraus entstehen automatisch Quick-Buttons in
//  der Karten-Vorschau und Generier-Panels im Detail-Drawer. Die eigentliche
//  Generierung läuft serverseitig (Cloud Function generateTemplatedEmail).
//
//  Diese Datei bündelt den Firebase-Pfad, das Normalisieren (Firebase liefert
//  Arrays je nach Keys mal als Array, mal als Objekt) und die Phasen-Auswahl,
//  damit Settings-Editor und StructureBoard dieselbe Logik nutzen.
// ─────────────────────────────────────────────────────────────
import { PHASES } from './structureConfig';

export const EMAIL_TEMPLATES_PATH = 'salesgod-research/emailTemplates';

// Kompakte, kollisionsarme ID für Vorlagen, Drop-Felder & generierte Instanzen.
export const genTplId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

// Alle wählbaren Ziel-„Sektionen" (= Pipeline-Überstatus) aus der Struktur-Config.
// So tauchen neue Phasen automatisch in der Vorlagen-Auswahl auf.
export const phaseOptions = () => {
  const out = [];
  PHASES.forEach((phase) => {
    (phase.superStatuses || []).forEach((s) => {
      out.push({ id: s.id, label: s.label, phaseLabel: phase.label });
    });
  });
  return out;
};

const labelForSuperStatus = (id) => {
  const opt = phaseOptions().find((o) => o.id === id);
  return opt ? opt.label : id;
};
export { labelForSuperStatus };

const asArray = (raw) => {
  if (!raw) return [];
  return Array.isArray(raw) ? raw : Object.values(raw);
};

// Rohe Vorlage aus Firebase -> sauberes Objekt mit garantierten Feldern/Typen.
export const normalizeTemplate = (id, raw) => {
  const t = raw && typeof raw === 'object' ? raw : {};
  return {
    id,
    name: String(t.name || ''),
    aiHint: String(t.aiHint || ''),
    styleSamples: asArray(t.styleSamples).map((s) => String(s || '')).filter((s) => s.trim()),
    inputSections: asArray(t.inputSections)
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        id: String(s.id || genTplId()),
        label: String(s.label || ''),
        required: s.required === true,
      }))
      .filter((s) => s.label.trim()),
    versions: Math.max(2, Math.min(5, Number(t.versions) || 3)),
    showIn: asArray(t.showIn).map((x) => String(x || '')).filter(Boolean),
    quickButton: t.quickButton !== false,
    numbered: t.numbered === true,
    active: t.active !== false,
    createdAt: typeof t.createdAt === 'number' ? t.createdAt : null,
    updatedAt: typeof t.updatedAt === 'number' ? t.updatedAt : null,
  };
};

// Firebase-Map -> Liste normalisierter Vorlagen (älteste zuerst angelegt zuerst).
export const normalizeTemplates = (map) => {
  const obj = map && typeof map === 'object' ? map : {};
  return Object.keys(obj)
    .map((id) => normalizeTemplate(id, obj[id]))
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
};

// Alle aktiven Vorlagen, die in einem bestimmten Überstatus angezeigt werden.
export const templatesForSuperStatus = (templates, superStatus) =>
  (Array.isArray(templates) ? templates : [])
    .filter((t) => t.active && Array.isArray(t.showIn) && t.showIn.includes(superStatus));

// Generierte Instanzen einer Vorlage am Kundenobjekt
// (customers/{slug}/generatedEmails/{tplId}) -> saubere Liste, neueste zuerst.
export const normalizeGeneratedInstances = (raw) => {
  const obj = raw && typeof raw === 'object' ? raw : null;
  if (!obj) return [];
  return Object.keys(obj)
    .map((id) => {
      const v = obj[id] && typeof obj[id] === 'object' ? obj[id] : {};
      const versions = asArray(v.versions)
        .filter((x) => x && typeof x === 'object')
        .map((x) => ({ subject: String(x.subject || ''), body: String(x.body || '') }));
      const rcp = v.recipient && typeof v.recipient === 'object' ? v.recipient : null;
      return {
        id,
        number: typeof v.number === 'number' ? v.number : 0,
        versions,
        selectedVersion: typeof v.selectedVersion === 'number' ? v.selectedVersion : 0,
        recipient: rcp ? { name: String(rcp.name || ''), role: String(rcp.role || ''), email: String(rcp.email || '') } : null,
        tone: v.tone === 'sie' ? 'sie' : (v.tone === 'du' ? 'du' : null),
        inputs: v.inputs && typeof v.inputs === 'object' ? v.inputs : {},
        generatedAt: typeof v.generatedAt === 'number' ? v.generatedAt : 0,
        done: v.done === true,
        doneAt: typeof v.doneAt === 'number' ? v.doneAt : null,
      };
    })
    .sort((a, b) => (b.number - a.number) || (b.generatedAt - a.generatedAt));
};
